import { slugify } from "@certn/utils";
import { css } from "@emotion/css";
import { isValidElement } from "react";
import { VisuallyHidden } from "react-aria";
import { ColumnProps, Key, TableBodyProps } from "react-aria-components";
import { useTranslation } from "react-i18next";

import { Cell, textAlignment } from "./assets/Cell";
import { Column } from "./assets/Column";
import { Row } from "./assets/Row";
import { Table, TableProps } from "./assets/Table";
import { TableBody } from "./assets/TableBody";
import { TableHeader } from "./assets/TableHeader";
import { Text } from "../../foundations/typography/Text";
import { Loader } from "../loaders/loading/Loader";
import { mq } from "../utils";

export interface ColumnDefinition<T> {
  allowsSorting?: ColumnProps["allowsSorting"];
  children?: ColumnDefinition<T>[];
  defaultWidth?: ColumnProps["defaultWidth"];
  id?: ColumnProps["id"];
  isAction?: boolean;
  isRowHeader?: ColumnProps["isRowHeader"];
  key: string;
  maxWidth?: ColumnProps["maxWidth"];
  minWidth?: ColumnProps["minWidth"];
  name?: React.ReactNode;
  render?(item: T): React.ReactNode;
  textValue?: string;
  width?: ColumnProps["width"];
  testID?: string;
  textAlignment?: textAlignment;
  displayRightBorder?: boolean;
  onClick?: (item: T) => void;
}
interface Props<T> extends TableProps {
  columns: ColumnDefinition<T>[];
  rowKey?: string;
  rows: T[];
  highlightedRows?: Key[];
  testId?: string;
  isLoading?: boolean;
  renderEmptyState?: TableBodyProps<T>["renderEmptyState"];
  minWidth?: string;
}

export function DataTable<T extends { id: Key }>(props: Props<T>) {
  const { t } = useTranslation();

  const {
    columns,
    rows,
    renderEmptyState,
    highlightedRows = [],
    testId = "data-table",
    rowKey = "id",
    minWidth = "934px",
  } = props;

  return (
    <div data-testid={testId} className={styles.scrollContainer}>
      <div
        className={css(
          mq({
            minWidth: [undefined, undefined, minWidth],
          })
        )}
      >
        <Table {...props}>
          <TableHeader columns={columns}>
            {(column) => {
              return (
                <Column
                  textAlignment={column.textAlignment}
                  displayRightBorder={column.displayRightBorder}
                  id={column.id || column.key}
                  allowsSorting={column.allowsSorting}
                  defaultWidth={
                    column.defaultWidth || column.isAction ? 48 : undefined
                  }
                  isRowHeader={column.isRowHeader}
                  key={column.key}
                  maxWidth={column.minWidth}
                  minWidth={column.maxWidth}
                  width={column.width}
                  textValue={
                    column.textValue ||
                    (typeof column.name === "string" ? column.name : column.key)
                  }
                  data-testid={column?.testID || undefined}
                >
                  {column.name || (
                    <VisuallyHidden>{column.textValue}</VisuallyHidden>
                  )}
                </Column>
              );
            }}
          </TableHeader>
          <TableBody
            items={rows}
            renderEmptyState={(renderProps) => {
              if (props.isLoading) {
                return (
                  <div className={styles.empty}>
                    <Loader />
                  </div>
                );
              }

              return (
                <div className={styles.empty}>
                  {renderEmptyState ? (
                    renderEmptyState(renderProps)
                  ) : (
                    <Text
                      style="global/body-secondary"
                      align="center"
                      color="text-weak"
                    >
                      {t("No results found.")}
                    </Text>
                  )}
                </div>
              );
            }}
          >
            {(item) => {
              const highlightedRowBackgroundColor = "background-warning-weak";
              const isHighlighted = highlightedRows.includes(item.id);
              const backgroundColor = isHighlighted
                ? highlightedRowBackgroundColor
                : undefined;

              return (
                <Row
                  key={item[rowKey as keyof typeof item] as Key}
                  id={item[rowKey as keyof typeof item] as Key}
                  columns={columns}
                  data-testid="data-table-row"
                  backgroundColor={backgroundColor}
                >
                  {({
                    key,
                    render,
                    isAction,
                    textAlignment,
                    displayRightBorder,
                    onClick,
                  }) => {
                    const itemValue = item[key as keyof typeof item];
                    const testID = `${testId}-${slugify(key)}-cell`;

                    if (render) {
                      return (
                        <Cell
                          textAlignment={textAlignment}
                          displayRightBorder={displayRightBorder}
                          data-testid={testID}
                          isAction={isAction}
                          key={key}
                          onClick={onClick && (() => onClick(item))}
                        >
                          {render(item)}
                        </Cell>
                      );
                    }

                    if (
                      isValidElement(itemValue) ||
                      typeof itemValue === "string"
                    ) {
                      return (
                        <Cell
                          data-testid={testID}
                          isAction={isAction}
                          textAlignment={textAlignment}
                          displayRightBorder={displayRightBorder}
                          onClick={onClick && (() => onClick(item))}
                        >
                          {itemValue}
                        </Cell>
                      );
                    }

                    return (
                      <Cell
                        data-testid={testID}
                        isAction={isAction}
                        displayRightBorder={displayRightBorder}
                        onClick={onClick && (() => onClick(item))}
                      >
                        {JSON.stringify(itemValue)}
                      </Cell>
                    );
                  }}
                </Row>
              );
            }}
          </TableBody>
        </Table>
      </div>
      {rows.length > 0 && props.isLoading && (
        <Loader overlay background="background-inverse-ghost-hover" />
      )}
    </div>
  );
}

const styles = {
  scrollContainer: css({ width: "100%", overflowX: "auto" }),
  empty: css({
    padding: "var(--space-6)",
    minHeight: 288,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: "var(--space-4)",
  }),
};
